import { cable } from '../cable'

// Owns the "subscribe once to an ActionCable channel" pattern that
// every Pinia store with a live feed duplicates today
// (`useWikiPagesStore.subscribe`, `useLintNotesStore.subscribe`,
// `useSourceActivityStore.subscribe`).
//
// Stores call this with their channel name + handlers; the
// composable guarantees the subscription is opened at most once per
// app lifetime (Pinia stores are singletons; idempotent calls keep
// `if (subscribed) return` out of every store body).
//
// Returns a `{ unsubscribe }` handle that tears down the underlying
// ActionCable subscription and releases the channel so a later
// subscribe() can re-open it. Most stores live for the app's lifetime
// and ignore it; per-session callers released on teardown
// (source_activity, dropped on admin sign-out) call it. When the
// channel was already open (idempotent call, or a second store sharing
// the name) the handle is an inert no-op — only the opener owns teardown.
//
// Usage:
//
//   useChannelSubscription('WikiPageChannel', {
//     received(data) { applyChange(data as ChangeMessage) },
//     connected() { void fetchAll() },  // re-fetch on reconnect
//   })
//
// The `received` handler runs for every broadcast on the channel;
// the optional `connected` handler runs on initial connect AND on
// reconnect after a transient WS drop. The reconnect-fetch pattern
// matters because broadcasts emitted during the drop would otherwise
// leave the store stale.

// A channel target is either a bare channel name (app-lifetime singleton
// feeds: WikiPageChannel, LintNoteChannel, …) or a `{ channel, params }`
// pair for a PARAMETERISED channel where the params are part of the
// subscription identity (e.g. AgentOutputChannel keyed by thread_id).
// Two targets with the same channel but different params are distinct
// subscriptions — the once-guard keys on the full identity, so a
// per-thread feed can re-subscribe when the active thread changes.
export type ChannelTarget = string | { channel: string; params?: Record<string, unknown> }

function identityKey(target: ChannelTarget): string {
  return typeof target === 'string' ? target : `${target.channel}:${JSON.stringify(target.params ?? {})}`
}

// ActionCable's `subscriptions.create` takes either a channel-name string
// or a `{ channel, ...params }` object; flatten params into that shape.
// The explicit `channel: string` keeps the result assignable to
// ActionCable's ChannelNameWithParams (a bare Record loses that guarantee).
function createArg(target: ChannelTarget): string | { channel: string; [key: string]: unknown } {
  return typeof target === 'string' ? target : { channel: target.channel, ...target.params }
}

export interface ChannelHandlers<T> {
  received: (data: T) => void
  connected?: () => void
  // Called on every connect AFTER the first. ActionCable fires
  // `connected` on the initial connect too, and stores already do an
  // explicit first-paint fetch — so refetching there would be a wasted
  // double-fetch. This fires only on genuine reconnects, re-fetching to
  // catch up on broadcasts emitted during a transient WS drop.
  // Folds the skip-first-then-refetch idiom that wiki_pages / lint_notes
  // / synthesis_suggestions each hand-rolled with their own
  // `firstConnectSeen` flag into this one place.
  refetchOnReconnect?: () => void
}

// Handle returned from `useChannelSubscription`. `unsubscribe` is the
// opener's teardown; for an already-open channel it's an inert no-op.
export interface ChannelSubscription {
  unsubscribe: () => void
}

const NO_OP_SUBSCRIPTION: ChannelSubscription = { unsubscribe: () => {} }

// Module-level so calling the composable from two stores subscribed
// to the SAME channel name still only opens one underlying
// subscription. Today no two stores share a channel; the guard is
// belt-and-braces for future use.
const openSubscriptions = new Set<string>()

export function useChannelSubscription<T = unknown>(
  target: ChannelTarget,
  handlers: ChannelHandlers<T>,
): ChannelSubscription {
  const key = identityKey(target)
  // Already open (idempotent re-call, or another store on the same
  // channel identity): this caller didn't open it, so it doesn't own teardown.
  if (openSubscriptions.has(key)) return NO_OP_SUBSCRIPTION
  openSubscriptions.add(key)

  // Per-subscription latch for `refetchOnReconnect`: the first
  // `connected` is the initial connect (skip), every later one is a
  // reconnect (refetch). `connected` itself still fires every time.
  let firstConnectSeen = false
  const subscription = cable().subscriptions.create(createArg(target), {
    received(data: T) {
      handlers.received(data)
    },
    connected() {
      handlers.connected?.()
      if (firstConnectSeen) {
        handlers.refetchOnReconnect?.()
      } else {
        firstConnectSeen = true
      }
    },
  })

  return {
    unsubscribe() {
      subscription.unsubscribe()
      openSubscriptions.delete(key)
    },
  }
}

// Test hook: vitest needs to reset the once-guard between specs
// because the module-level Set persists across test files. Not part
// of the production interface — exported under a `_test_only_`
// prefix so the dependency is visible.
export function _test_only_resetSubscriptions(): void {
  openSubscriptions.clear()
}
